import { ActionTypes } from "./plan.actions";
import { Action } from "@ngrx/store";
import { Discount, ItemOptions, PlanItem, PlanStage } from "./plan.model";
import { ActionTypes as OdontogramActionTypes } from "../../odontogram/odontogram.actions";
import { ActionTypes as PlanEditorActionTypes } from "../plan-editor.actions";

export interface State {
  currentGroupIdentifier: string;
  currentTreatmentIdentifier: string;
  currentItem: PlanItem;
  stages: PlanStage[];
  customTotalText?: string;
  discount?: Discount;
  showItemList: boolean;
  useOdontogram: boolean;
  mainComponent: string;
  loading: boolean;
}

export const initialState: State = {
  currentGroupIdentifier: "hygiene",
  currentTreatmentIdentifier: null,
  currentItem: new PlanItem(),
  stages: [],
  customTotalText: null,
  discount: new Discount(),
  showItemList: false,
  useOdontogram: true,
  mainComponent: "odontogram",
  loading: false,
};

export function reducer(state: State = initialState, action: Action): State {
  switch (action.type) {
    case ActionTypes.SELECT_GROUP:
      return Object.assign({}, state, {
        currentGroupIdentifier: action.payload.identifier,
        useOdontogram: action.payload.useOdontogram,
        mainComponent: "odontogram",
      });

    case ActionTypes.SELECT_ITEM:
      const updatedCurrentItem = Object.assign({}, state.currentItem, {
        treatment: action.payload.custom
          ? action.payload.custom.parent
          : action.payload.identifier,
        options: new ItemOptions(),
        name: action.payload.custom ? action.payload.custom.name : null,
        customTreatmentId: action.payload.custom
          ? action.payload.custom.id
          : null,
        customTreatmentPrice: action.payload.custom
          ? action.payload.custom.price
          : null,
      });
      if (action.payload.custom && action.payload.custom.parent === "other") {
        updatedCurrentItem.options.name = action.payload.custom.name;
      }
      return Object.assign({}, state, {
        currentTreatmentIdentifier: action.payload.identifier,
        currentItem: updatedCurrentItem,
        mainComponent: action.payload.mainComponent,
      });

    case ActionTypes.UPDATE_OPTIONS:
      const updatedCurrentItem2 = Object.assign({}, state.currentItem, {
        options: Object.assign({}, state.currentItem.options, action.payload),
      });
      return Object.assign({}, state, { currentItem: updatedCurrentItem2 });

    case ActionTypes.ADD_ITEM:
      return Object.assign({}, state, { loading: true });

    case ActionTypes.ADD_ITEM_SUCCESS:
      return Object.assign({}, state, {
        stages: state.stages.map((stage) =>
          stage.id !== action.payload.stage
            ? stage
            : Object.assign({}, stage, {
                items: [...stage.items, action.payload],
              }),
        ),
        currentItem: new PlanItem(),
        currentTreatmentIdentifier: null,
        mainComponent: "odontogram",
        loading: false,
      });

    case ActionTypes.ADD_ITEM_FAIL:
      return Object.assign({}, state, { loading: false });

    case ActionTypes.UPDATE_ITEM_SUCCESS:
      return Object.assign({}, state, {
        stages: state.stages.map((stage) => {
          // return item.id === action.payload.id ? Object.assign({}, item, action.payload) : item;
          return Object.assign({}, stage, {
            items: stage.items.map((item) => {
              return item.id === action.payload.id
                ? Object.assign({}, item, action.payload)
                : item;
            }),
          });
        }),
        loading: false,
      });

    case ActionTypes.DELETE_ITEM:
      return Object.assign({}, state, { loading: true });

    case ActionTypes.DELETE_ITEM_SUCCESS:
      return Object.assign({}, state, {
        stages: state.stages.map((stage) => {
          return Object.assign({}, stage, {
            items: stage.items.filter((item) => item.id !== action.payload.id),
          });
        }),
        loading: false,
      });

    case ActionTypes.DELETE_ITEM_FAIL:
      return Object.assign({}, state, { loading: false });

    case ActionTypes.SORT_ITEMS_SUCCESS:
      return Object.assign({}, state, {
        stages: state.stages.map((stage) => {
          // return item.id === action.payload.id ? Object.assign({}, item, action.payload) : item;
          return stage.id !== action.payload.stage_id
            ? stage
            : Object.assign({}, stage, { items: action.payload.items });
        }),
        loading: false,
      });
    /*
         case ActionTypes.LOAD_ITEMS_SUCCESS:
         return Object.assign({}, state, {items: action.payload});
         */

    case ActionTypes.SHOW_LIST:
      return Object.assign({}, state, { showItemList: true });

    case ActionTypes.HIDE_LIST:
      return Object.assign({}, state, { showItemList: false });

    case OdontogramActionTypes.TEETH_SELECTION_UPDATED:
      const currentItem = Object.assign({}, state.currentItem, {
        teeth: action.payload,
      });
      return Object.assign({}, state, { currentItem: currentItem });

    case PlanEditorActionTypes.LOAD_SUCCESS:
      return Object.assign({}, state, {
        stages: action.payload.stages,
        customTotalText: action.payload.customTotalText,
        discount: action.payload.discount,
      });

    case PlanEditorActionTypes.CHANGE_STEP:
      return Object.assign({}, state, {
        currentGroupIdentifier: "hygiene",
        currentTreatmentIdentifier: null,
        currentItem: new PlanItem(),
        showItemList: false,
        useOdontogram: true,
        mainComponent: "odontogram",
      });

    case ActionTypes.ADD_STAGE:
      return Object.assign({}, state, { loading: true });

    case ActionTypes.ADD_STAGE_SUCCESS:
      return Object.assign({}, state, {
        stages: [...state.stages, action.payload],
        loading: false,
      });

    case ActionTypes.ADD_STAGE_FAIL:
      return Object.assign({}, state, { loading: false });

    case ActionTypes.UPDATE_STAGE_SUCCESS:
      return Object.assign({}, state, {
        stages: state.stages.map((stage) => {
          return stage.id === action.payload.id
            ? Object.assign({}, stage, action.payload)
            : stage;
        }),
        loading: false,
      });

    case ActionTypes.DELETE_STAGE:
      return Object.assign({}, state, { loading: true });

    case ActionTypes.DELETE_STAGE_SUCCESS:
      return Object.assign({}, state, {
        stages: state.stages.filter((stage) => stage.id !== action.payload.id),
        loading: false,
      });

    case ActionTypes.SORT_STAGES_SUCCESS:
      return Object.assign({}, state, { stages: [...action.payload] });

    case ActionTypes.UPDATE_PLAN_DETAILS_SUCCESS:
      return Object.assign({}, state, {
        customTotalText: action.payload.customTotalText,
        discount: action.payload.discount,
      });

    default:
      return state;
  }
}

export const getCurrentGroupIdentifier = (state: State) =>
  state.currentGroupIdentifier;
export const getCurrentTreatmentIdentifier = (state: State) =>
  state.currentTreatmentIdentifier;
export const getCurrentItem = (state: State) => state.currentItem;
export const getStages = (state: State) => state.stages;
export const getCustomTotalText = (state: State) => state.customTotalText;
export const getDiscount = (state: State) => state.discount;
export const getShowItemList = (state: State) => state.showItemList;
export const getUseOdontogram = (state: State) => state.useOdontogram;
export const getMainComponent = (state: State) => state.mainComponent;
export const getIsLoading = (state: State) => state.loading;



// WEBPACK FOOTER //
// /app/src/app/core/store/plan-editor/plan/plan.reducer.ts